import {
  isLikelySeoTagline,
  isLikelyServiceHoursLabel,
  isWeakUrlDerivedBusinessName,
  normalizeHireCompanyDisplayName,
  pickBestBusinessNameCandidate,
  resolveHireBusinessDisplayName,
} from "~/lib/marketplace/hire-company-name";
import { suggestedOrganizationNameFromWebsiteUrl } from "~/lib/onboarding/org-name-from-website";

const US_STATES = "AL|AK|AZ|AR|CA|CO|CT|DE|FL|GA|HI|ID|IL|IN|IA|KS|KY|LA|ME|MD|MA|MI|MN|MS|MO|MT|NE|NV|NH|NJ|NM|NY|NC|ND|OH|OK|OR|PA|RI|SC|SD|TN|TX|UT|VT|VA|WA|WV|WI|WY|DC";
const CA_PROVINCES = "ON|BC|AB|QC|MB|SK|NS|NB|NL|PE|NT|NU|YT";

const US_CITY_STATE_ZIP_REGEX = new RegExp(
  `\\b([A-Za-z][A-Za-z .'-]{2,45}),\\s*(${US_STATES})\\s+(\\d{5})(?:-\\d{4})?\\b`,
  "g",
);

/** e.g. "Denver CO 80203" (comma omitted between city and state — common in footers). */
const US_CITY_STATE_ZIP_NO_COMMA_REGEX = new RegExp(
  `\\b([A-Za-z][A-Za-z .'-]{2,45})\\s+(${US_STATES})\\s+(\\d{5})(?:-\\d{4})?\\b`,
  "g",
);

const CA_CITY_PROV_POSTAL_REGEX = new RegExp(
  `\\b([A-Za-z][A-Za-z .'-]{2,45}),\\s*(${CA_PROVINCES})\\s+([A-Z]\\d[A-Z]\\s*\\d[A-Z]\\d)\\b`,
  "gi",
);

const CA_POSTAL_REGEX = /\b([A-Z]\d[A-Z]\s*\d[A-Z]\d)\b/i;

const CITY_STATE_ZIP_SKIP =
  /^(Mon|Tue|Wed|Thu|Fri|Sat|Sun|Mon-Fri|January|February|March|April|May|June|July|August|September|October|November|December|United States)$/i;

const US_STREET_TOKEN =
  /^(st\.?|street|rd\.?|road|ave\.?|avenue|blvd\.?|boulevard|ln\.?|lane|dr\.?|drive|way|ct\.?|court|floor|fl\.?|suite|ste\.?|unit|#|n|s|e|w|ne|nw|se|sw)$/i;

export type DetectedWebsiteLocation = {
  name: string | null;
  address: string;
  city: string;
  state: string;
  displayLabel: string;
};

export function decodeHtmlEntities(value: string): string {
  return value
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/gi, "'")
    .replace(/&#8211;/g, "–")
    .replace(/&#8212;/g, "—")
    .replace(/&#x2013;/gi, "–")
    .replace(/&#x2014;/gi, "—")
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
    .replace(/&#x([0-9a-f]+);/gi, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
}

/** Collapsed website nav labels mistaken for office names (e.g. AllVitae contact pages). */
const NAV_MENU_LOCATION_WORDS = new Set([
  "home",
  "about",
  "menu",
  "testimonials",
  "testimonial",
  "services",
  "service",
  "contact",
  "blog",
  "faq",
  "faqs",
  "gallery",
  "team",
  "meet",
  "the",
  "office",
  "locations",
  "location",
  "our",
  "us",
  "careers",
  "privacy",
  "policy",
  "login",
  "portal",
  "specials",
  "special",
  "new",
  "patients",
  "patient",
  "book",
  "online",
  "schedule",
]);

/**
 * True when a "location name" is actually collapsed nav/menu chrome
 * (e.g. "Home About Meet the Team Testimonials Office").
 */
export function isLikelyNavMenuLocationName(
  name: string | null | undefined,
): boolean {
  const n = decodeHtmlEntities((name ?? "").trim());
  if (!n) return false;
  if (/^(menu\s+)?home\s+about\b/i.test(n)) return true;
  if (
    /\bmeet the team\b/i.test(n) &&
    /\b(home|about|testimonials?|menu|services)\b/i.test(n)
  ) {
    return true;
  }
  const words = n
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter(Boolean);
  if (words.length < 3) return false;
  const navHits = words.filter((w) => NAV_MENU_LOCATION_WORDS.has(w));
  // Most words are nav chrome, or many distinct nav tokens appear together.
  if (navHits.length >= 3 && navHits.length / words.length >= 0.6) return true;
  if (navHits.length >= 4) return true;
  return false;
}

function normalizeStreetDedupeKey(address: string): string {
  let normalized = address
    .toLowerCase()
    .replace(/[#.]/g, " ")
    .replace(/\bsuite\b/g, "ste")
    .replace(/\bcircle\b/g, "cir")
    .replace(/\bavenue\b/g, "ave")
    .replace(/\bstreet\b/g, "st")
    .replace(/\broad\b/g, "rd")
    .replace(/\bdrive\b/g, "dr")
    .replace(/\s+/g, " ")
    .trim();

  // Collapse "… Cir Ste 101, Lutz, FL 33558, Lutz, FL 33558" → single city/ZIP trailer.
  normalized = normalized.replace(
    /,\s*([a-z .'-]+),\s*([a-z]{2})\s+(\d{5})(?:-\d{4})?(?:\s*,\s*\1,\s*\2\s+\3(?:-\d{4})?)+$/i,
    ", $1, $2 $3",
  );
  return normalized;
}

/** Strip duplicated trailing "City, ST ZIP" glued onto an already-complete address. */
function canonicalizeStreetAddress(address: string): string {
  const trimmed = address.replace(/\s+/g, " ").trim();
  const usTrailer = trimmed.match(
    /^(.+,\s*[A-Za-z .'-]+,\s*[A-Z]{2}\s+\d{5}(?:-\d{4})?)(?:\s*,\s*[A-Za-z .'-]+,\s*[A-Z]{2}\s+\d{5}(?:-\d{4})?)+$/,
  );
  if (usTrailer?.[1]) return usTrailer[1].trim();
  return trimmed;
}

function locationStateZipKey(loc: DetectedWebsiteLocation): string | null {
  const zip =
    loc.address.match(/\b(\d{5})\b/)?.[1] ??
    loc.displayLabel.match(/\b(\d{5})\b/)?.[1];
  if (!zip || !loc.state) return null;
  return `${loc.state.toUpperCase()}|${zip}`;
}

function locationCityStateZipKey(loc: DetectedWebsiteLocation): string | null {
  const stateZip = locationStateZipKey(loc);
  if (!stateZip) return null;
  const city = loc.city.replace(/\s+/g, " ").trim().toLowerCase();
  if (!city) return stateZip;
  return `${city}|${stateZip}`;
}

const GENERIC_PAGE_TITLE_PREFIX =
  /^(contact(?:\s+us)?|home|about(?:\s+us)?|welcome|locations?|services?|our\s+team|team)$/i;

/** Strip SEO page prefixes like "Contact - Business Name" → business name segment. */
export function stripGenericPageTitlePrefix(title: string): string {
  const decoded = decodeHtmlEntities(title.trim());
  const parts = decoded.split(/\s*[-–—|]\s*/).map((part) => part.trim()).filter(Boolean);
  if (parts.length >= 2 && GENERIC_PAGE_TITLE_PREFIX.test(parts[0] ?? "")) {
    return parts.slice(1).join(" - ").trim();
  }
  return decoded;
}

/** When street + city run together on one line (e.g. "Elm Street Ardsley"), keep only the city. */
function sanitizeUsCity(raw: string): string {
  const parts = raw.trim().split(/\s+/);
  if (parts.length <= 1) return raw.trim();

  let lastStreetIdx = -1;
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i]!;
    if (US_STREET_TOKEN.test(part) || /^\d+[A-Za-z]?$/.test(part)) {
      lastStreetIdx = i;
    }
  }

  const cityParts = parts.slice(lastStreetIdx + 1);
  if (cityParts.length >= 1 && cityParts.length <= 4) {
    return cityParts.join(" ");
  }

  return raw.trim();
}

function normalizeCanadianPostal(raw: string): string {
  const compact = raw.replace(/\s/g, "").toUpperCase();
  if (compact.length !== 6) return raw.toUpperCase();
  return `${compact.slice(0, 3)} ${compact.slice(3)}`;
}

function formatCapsLocationName(raw: string): string {
  return raw
    .split(/[•·]/)
    .map((part) =>
      part
        .trim()
        .split(/\s+/)
        .map((word) => {
          if (/^[A-Z]\.?$/.test(word) || word === "ST." || word === "ST") return word;
          return word.charAt(0) + word.slice(1).toLowerCase();
        })
        .join(" "),
    )
    .join(" • ");
}

function extractLocationNameBeforeIndex(text: string, idx: number): string | null {
  if (idx <= 0) return null;
  const prefix = text.slice(Math.max(0, idx - 120), idx).trim();

  const capsMatch = prefix.match(/([A-Z][A-Z0-9 &•·\-™®]{4,70})\s*$/);
  if (capsMatch?.[1]) {
    const candidate = capsMatch[1].trim();
    if (candidate.length >= 4 && candidate.length <= 70) {
      const formatted = formatCapsLocationName(candidate);
      if (!isLikelyNavMenuLocationName(formatted)) return formatted;
    }
  }

  const nameMatch = prefix.match(
    /([A-Z][A-Za-z0-9 /&–—\-™®]+(?:County|Office|Location)?)\s*$/,
  );
  const candidate = nameMatch?.[1]?.trim();
  if (candidate && candidate.length >= 3 && candidate.length <= 55) {
    const cleaned = candidate
      .replace(/\s+(By appt only\.?|Appointment only\.?)$/i, "")
      .trim();
    if (!isLikelyNavMenuLocationName(cleaned)) return cleaned;
  }

  return null;
}

function locationDedupeKey(loc: DetectedWebsiteLocation): string {
  // Prefer full street address — multiple offices in one city often share a ZIP (e.g. Nashville 37203).
  const streetKey = normalizeStreetDedupeKey(loc.address);
  if (streetKey.length >= 8) return streetKey;

  const caPostal = loc.displayLabel.match(CA_POSTAL_REGEX)?.[1];
  if (caPostal && loc.state) {
    return `${loc.state.toUpperCase()}-${caPostal.replace(/\s/g, "").toUpperCase()}`;
  }
  const usZip = loc.displayLabel.match(/\b(\d{5})\b/)?.[1] ?? loc.address.match(/\b(\d{5})\b/)?.[1];
  if (usZip && loc.state && loc.city) {
    const nameKey = loc.name?.replace(/\s+/g, " ").trim().toLowerCase();
    if (nameKey) return `${nameKey}|${loc.state}-${usZip}`;
    return `${loc.city.replace(/\s+/g, " ").trim().toLowerCase()}|${loc.state}-${usZip}`;
  }
  if (usZip && loc.state) return `${loc.state}-${usZip}`;
  return loc.displayLabel;
}

/** Split ZIP glued to next city label (e.g. "80203Frisco:" → "80203\nFrisco:"). */
function normalizeGluedZipCityBoundaries(text: string): string {
  return text.replace(/(\d{5}(?:-\d{4})?)([A-Z][a-zA-Z])/g, "$1\n$2");
}

/** US and Canadian street addresses for location counting from crawled website text. */
export function extractAddressCandidates(text: string): string[] {
  const candidates = new Set<string>();
  const normalized = normalizeGluedZipCityBoundaries(text);

  // Include en/em dashes (common on "Ct – Ste 411" address lines).
  const usSingleLineRegex =
    /\b\d{1,6}\s+[A-Za-z0-9.#'’\-/–— ]+,\s*[A-Za-z .'-]+,\s*[A-Z]{2}\s+\d{5}(?:-\d{4})?\b/g;
  for (const match of normalized.matchAll(usSingleLineRegex)) {
    const value = match[0]?.replace(/\s+/g, " ").trim();
    if (value) candidates.add(value);
  }

  // Street + "City ST ZIP" without comma after city (footer style).
  const usNoCommaCityRegex = new RegExp(
    `\\b\\d{1,6}\\s+[A-Za-z0-9.#'’\\-/–— ]+,\\s*[A-Za-z .'-]+\\s+(${US_STATES})\\s+\\d{5}(?:-\\d{4})?\\b`,
    "g",
  );
  for (const match of normalized.matchAll(usNoCommaCityRegex)) {
    const value = match[0]?.replace(/\s+/g, " ").trim();
    if (value) candidates.add(value);
  }

  // "Denver: 517 E 16th Ave, Denver CO 80203" on one logical line.
  const labeledStreetCityRegex = new RegExp(
    `\\b([A-Za-z][A-Za-z .'-]{1,40})\\s*:\\s*(\\d{1,6}\\s+[A-Za-z0-9.#'’\\-/ ]+?),\\s*([A-Za-z .'-]+)\\s+(${US_STATES})\\s+(\\d{5})(?:-\\d{4})?\\b`,
    "g",
  );
  for (const match of normalized.matchAll(labeledStreetCityRegex)) {
    const street = match[2]?.replace(/\s+/g, " ").trim();
    const city = match[3]?.replace(/\s+/g, " ").trim();
    const state = match[4];
    const zip = match[5];
    if (street && city && state && zip) {
      candidates.add(`${street}, ${city}, ${state} ${zip}`);
    }
  }

  const caSingleLineRegex = new RegExp(
    `\\b\\d{1,6}[A-Za-z]?\\s+[A-Za-z0-9.#'’\\-/ ]+,\\s*[A-Za-z .'-]+,\\s*(${CA_PROVINCES})\\s+[A-Z]\\d[A-Z]\\s*\\d[A-Z]\\d\\b`,
    "gi",
  );
  for (const match of normalized.matchAll(caSingleLineRegex)) {
    const value = match[0]?.replace(/\s+/g, " ").trim();
    if (value) candidates.add(value);
  }

  const lines = normalized
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  for (let i = 0; i < lines.length - 1; i++) {
    const first = lines[i]!.replace(/^([A-Za-z][A-Za-z .'-]{1,40})\s*:\s*/, "");
    const second = lines[i + 1]!;

    if (
      /^\d{1,6}\s+/.test(first) &&
      /^[A-Za-z .'-]+,\s*[A-Z]{2}\s+\d{5}(?:-\d{4})?$/.test(second)
    ) {
      candidates.add(`${first}, ${second}`);
    }

    // Second line: "Denver CO 80203" (no comma).
    if (
      /^\d{1,6}\s+/.test(first) &&
      new RegExp(`^[A-Za-z .'-]+\\s+(${US_STATES})\\s+\\d{5}(?:-\\d{4})?$`).test(second)
    ) {
      const cityStateZip = second.replace(
        new RegExp(`^([A-Za-z .'-]+)\\s+(${US_STATES})\\s+(\\d{5}(?:-\\d{4})?)$`),
        "$1, $2 $3",
      );
      candidates.add(`${first.replace(/,\s*$/, "")}, ${cityStateZip}`);
    }

    const caSecondLine = new RegExp(
      `^[A-Za-z .'-]+,\\s*(${CA_PROVINCES})\\s+[A-Z]\\d[A-Z]\\s*\\d[A-Z]\\d$`,
      "i",
    );
    if (/^\d{1,6}[A-Za-z]?\s+/.test(first) && caSecondLine.test(second)) {
      candidates.add(`${first}, ${second}`);
    }
  }

  return Array.from(candidates, (value) => canonicalizeStreetAddress(value)).filter(
    (value, index, arr) => arr.indexOf(value) === index,
  );
}

/** e.g. "Ardsley, NY 10502" or "West Harrison, NY 10604" (no street line). */
export function extractCityStateZipLocations(text: string): DetectedWebsiteLocation[] {
  const results: DetectedWebsiteLocation[] = [];
  const seen = new Set<string>();
  const normalized = normalizeGluedZipCityBoundaries(text);

  const addCityStateZip = (cityRaw: string, state: string, zip: string) => {
    const city = sanitizeUsCity(cityRaw.trim());
    if (CITY_STATE_ZIP_SKIP.test(city)) return;
    // Avoid treating street fragments as cities ("Mile Drive" from Frisco address lines).
    if (US_STREET_TOKEN.test(city.split(/\s+/).pop() ?? "")) return;
    const key = `${state}-${zip}`;
    if (seen.has(key)) return;
    seen.add(key);
    results.push({
      name: city,
      address: "",
      city,
      state,
      displayLabel: `${city}, ${state} ${zip}`,
    });
  };

  for (const match of normalized.matchAll(US_CITY_STATE_ZIP_REGEX)) {
    addCityStateZip(match[1]!, match[2]!, match[3]!);
  }

  for (const match of normalized.matchAll(US_CITY_STATE_ZIP_NO_COMMA_REGEX)) {
    addCityStateZip(match[1]!, match[2]!, match[3]!);
  }

  return results;
}

/** e.g. "Brampton, ON L7A 3P1" or "Vaughan, On L4H 3T4" (Canadian postal codes). */
export function extractCityProvincePostalLocations(text: string): DetectedWebsiteLocation[] {
  const results: DetectedWebsiteLocation[] = [];
  const seen = new Set<string>();

  for (const match of text.matchAll(CA_CITY_PROV_POSTAL_REGEX)) {
    const city = match[1]!.trim();
    const province = match[2]!.toUpperCase();
    const postal = normalizeCanadianPostal(match[3]!);
    if (CITY_STATE_ZIP_SKIP.test(city)) continue;

    const key = `${province}-${postal.replace(/\s/g, "")}`;
    if (seen.has(key)) continue;
    seen.add(key);

    const matchIndex = match.index ?? text.indexOf(match[0]!);
    const name = extractLocationNameBeforeIndex(text, matchIndex);

    results.push({
      name,
      address: "",
      city,
      state: province,
      displayLabel: name
        ? `${name} — ${city}, ${province} ${postal}`
        : `${city}, ${province} ${postal}`,
    });
  }

  return results;
}

function buildLocationDisplayLabel(loc: {
  name: string | null;
  address: string;
  city: string;
  state: string;
}): string {
  if (loc.name) {
    return `${loc.name} — ${loc.city ? `${loc.city}, ${loc.state}` : loc.address}`;
  }
  if (loc.city && loc.state) return `${loc.city}, ${loc.state}`;
  return loc.address;
}

function isStrongerLocationName(
  nextName: string | null,
  existingName: string | null,
): boolean {
  if (!nextName || isLikelyNavMenuLocationName(nextName)) return false;
  if (!existingName || isLikelyNavMenuLocationName(existingName)) return true;
  return nextName.length > existingName.length;
}

/** Parse named locations + addresses from contact/locations page text. */
export function extractDetectedLocations(text: string): DetectedWebsiteLocation[] {
  const results: DetectedWebsiteLocation[] = [];
  const seen = new Set<string>();

  const addLocation = (loc: DetectedWebsiteLocation) => {
    const name =
      loc.name && isLikelyNavMenuLocationName(loc.name) ? null : loc.name;
    const next: DetectedWebsiteLocation = {
      ...loc,
      name,
      displayLabel: buildLocationDisplayLabel({ ...loc, name }),
    };

    const dedupeKey = locationDedupeKey(next);
    if (seen.has(dedupeKey)) {
      const existingIdx = results.findIndex((r) => locationDedupeKey(r) === dedupeKey);
      if (existingIdx >= 0 && isStrongerLocationName(next.name, results[existingIdx]!.name)) {
        const existing = results[existingIdx]!;
        results[existingIdx] = {
          ...existing,
          name: next.name,
          displayLabel: buildLocationDisplayLabel({
            name: next.name,
            address: existing.address || next.address,
            city: existing.city || next.city,
            state: existing.state || next.state,
          }),
        };
      }
      return;
    }
    seen.add(dedupeKey);
    results.push(next);
  };

  for (const rawAddress of extractAddressCandidates(text)) {
    const address = canonicalizeStreetAddress(rawAddress);
    const usZipMatch = address.match(/,\s*([A-Z]{2})\s+(\d{5})/);
    const usCityMatch = address.match(/,\s*([^,]+),\s*([A-Z]{2})\s+\d{5}/);

    const caMatch = address.match(
      new RegExp(`,\\s*([^,]+),\\s*(${CA_PROVINCES})\\s+([A-Z]\\d[A-Z]\\s*\\d[A-Z]\\d)`, "i"),
    );

    const city = usCityMatch?.[1]?.trim() ?? caMatch?.[1]?.trim() ?? "";
    const state = usCityMatch?.[2] ?? caMatch?.[2]?.toUpperCase() ?? usZipMatch?.[1] ?? "";

    const idx = text.indexOf(rawAddress);
    const name = idx > 0 ? extractLocationNameBeforeIndex(text, idx) : null;

    addLocation({
      name,
      address,
      city,
      state,
      displayLabel: buildLocationDisplayLabel({ name, address, city, state }),
    });
  }

  const streetCityZipKeys = new Set(
    results
      .filter((loc) => loc.address.trim().length >= 8)
      .map((loc) => locationCityStateZipKey(loc))
      .filter((key): key is string => Boolean(key)),
  );
  const streetStateZipKeys = new Set(
    results
      .filter((loc) => loc.address.trim().length >= 8)
      .map((loc) => locationStateZipKey(loc))
      .filter((key): key is string => Boolean(key)),
  );

  for (const loc of extractCityStateZipLocations(text)) {
    const cityKey = locationCityStateZipKey(loc);
    const stateZipKey = locationStateZipKey(loc);
    // Drop city-only rows when we already have a street-backed office for that city/ZIP.
    if (cityKey && streetCityZipKeys.has(cityKey)) continue;
    if (stateZipKey && streetStateZipKeys.has(stateZipKey)) continue;
    // Final guard: any street-backed row already covers this US ZIP.
    const zip = loc.displayLabel.match(/\b(\d{5})\b/)?.[1];
    if (
      zip &&
      results.some(
        (existing) =>
          existing.address.trim().length >= 8 &&
          (!existing.state || existing.state === loc.state) &&
          /\b\d{5}\b/.test(existing.address) &&
          existing.address.includes(zip),
      )
    ) {
      continue;
    }
    addLocation(loc);
  }

  for (const loc of extractCityProvincePostalLocations(text)) {
    addLocation(loc);
  }

  return results;
}

function uniqueCityStateZipCount(text: string): number {
  const keys = new Set<string>();
  for (const match of text.matchAll(
    /,\s*([A-Za-z .'/\-]+),\s*([A-Z]{2})\s+(\d{5})(?:-\d{4})?\b/g,
  )) {
    keys.add(`${match[2]}-${match[3]}`);
  }
  return keys.size;
}

function uniqueCanadianProvPostalCount(text: string): number {
  const keys = new Set<string>();
  for (const match of text.matchAll(CA_CITY_PROV_POSTAL_REGEX)) {
    const province = match[2]!.toUpperCase();
    const postal = match[3]!.replace(/\s/g, "").toUpperCase();
    keys.add(`${province}-${postal}`);
  }
  return keys.size;
}

function uniqueStateZipCount(text: string): number {
  const keys = new Set<string>();
  for (const match of text.matchAll(/\b([A-Z]{2})\s+(\d{5})(?:-\d{4})?\b/g)) {
    keys.add(`${match[1]}-${match[2]}`);
  }
  return keys.size;
}

/** Best-effort location count from homepage + contact/locations crawl text. */
export function inferLocationCountFromContent(contentText: string): number {
  const detected = extractDetectedLocations(contentText);
  if (detected.length >= 1) return detected.length;

  const addresses = extractAddressCandidates(contentText);
  if (addresses.length >= 2) return addresses.length;

  const cityStateZipCount = uniqueCityStateZipCount(contentText);
  if (cityStateZipCount >= 2) return cityStateZipCount;

  const caProvPostalCount = uniqueCanadianProvPostalCount(contentText);
  if (caProvPostalCount >= 2) return caProvPostalCount;

  const stateZipCount = uniqueStateZipCount(contentText);
  if (stateZipCount >= 2) return stateZipCount;

  if (addresses.length === 1) return 1;

  const lower = contentText.toLowerCase();
  const multiLocationHint =
    (lower.match(/\blocations?\b/g)?.length ?? 0) >= 2 ||
    contentIncludes(contentText, ["multiple offices", "our offices", "branch office", "all locations"]);

  return multiLocationHint ? 2 : 1;
}

function contentIncludes(content: string, terms: string[]): boolean {
  const lower = content.toLowerCase();
  return terms.some((term) => lower.includes(term));
}

export function extractMetaFromHtml(html: string): {
  title: string | null;
  siteName: string | null;
  description: string | null;
  imageUrl: string | null;
} {
  const titleMatch = html.match(/<title[^>]*>([^<]+)<\/title>/i);
  const siteNameMatch = html.match(
    /<meta[^>]+property=["']og:site_name["'][^>]+content=["']([^"']+)["']/i,
  );
  const descriptionMatch = html.match(
    /<meta[^>]+name=["']description["'][^>]+content=["']([^"']+)["']/i,
  );
  const ogImageMatch = html.match(
    /<meta[^>]+property=["']og:image["'][^>]+content=["']([^"']+)["']/i,
  );
  return {
    title: titleMatch?.[1] ? decodeHtmlEntities(titleMatch[1].trim()) : null,
    siteName: siteNameMatch?.[1] ? decodeHtmlEntities(siteNameMatch[1].trim()) : null,
    description: descriptionMatch?.[1] ? decodeHtmlEntities(descriptionMatch[1].trim()) : null,
    imageUrl: ogImageMatch?.[1] ? decodeHtmlEntities(ogImageMatch[1].trim()) : null,
  };
}

export function extractPrimaryPhoneFromText(text: string): string | undefined {
  const match = text.match(/\(?(\d{3})\)?[-.\s]?(\d{3})[-.\s]?(\d{4})/);
  if (!match) return undefined;
  return `(${match[1]}) ${match[2]}-${match[3]}`;
}

/** Prefer org/site name from crawl metadata over URL slug or SEO title. */
export function extractBusinessNameFromCrawl(
  websiteUrl: string,
  combinedText: string,
  meta: { title: string | null; siteName: string | null },
): string {
  const candidates: string[] = [];

  // og:site_name is the strongest crawl signal when it is a real brand (not an SEO tagline).
  if (meta.siteName) {
    const site = decodeHtmlEntities(meta.siteName).trim();
    if (
      site &&
      site.length >= 2 &&
      site.length <= 80 &&
      !isLikelySeoTagline(site) &&
      !isLikelyServiceHoursLabel(site) &&
      !isWeakUrlDerivedBusinessName(site, websiteUrl)
    ) {
      return normalizeHireCompanyDisplayName(site, websiteUrl) || site;
    }
  }

  const contactForMatch = meta.title?.match(/\bContact\s+(.+?)\s+for\b/i);
  if (contactForMatch?.[1]) {
    candidates.push(decodeHtmlEntities(contactForMatch[1].trim()));
  }

  if (meta.title) {
    const fromTitle = stripGenericPageTitlePrefix(meta.title);
    candidates.push(fromTitle);
    for (const segment of meta.title.split(/\s*[-–—|]\s*/)) {
      candidates.push(decodeHtmlEntities(segment.trim()));
    }
  }

  const firstLine = combinedText.split("\n").map((line) => line.trim()).find(Boolean);
  if (firstLine) {
    for (const segment of firstLine.split(/\||\u2013|\u2014|-/)) {
      candidates.push(decodeHtmlEntities(segment.trim()));
    }
  }

  candidates.push(suggestedOrganizationNameFromWebsiteUrl(websiteUrl));

  const picked = pickBestBusinessNameCandidate(candidates, websiteUrl);
  if (picked) {
    return normalizeHireCompanyDisplayName(picked, websiteUrl) || picked;
  }

  return resolveHireBusinessDisplayName(undefined, websiteUrl);
}
